import { Box, Card, CardContent, Typography } from "@mui/material";
import type { EChartsOption } from "echarts";
import ReactECharts from "echarts-for-react";
import { useMemo } from "react";
import type { IncentiveTankData } from "../models/MillOverview";
import { IncPercentPill } from "./IncPercentPill";
import {
	DEFAULT_INCENTIVE_THRESHOLDS,
	getZoneColor,
	SURFACE,
} from "./incentiveColors";

type Props = {
	data: IncentiveTankData | null;
};

// Horizontal Finished-vs-Target bars, rendered below the TPH chart.
export const IncentiveTankChart = ({ data }: Props) => {
	const thresholds = data
		? {
				Red: data.IncRed,
				Orange: data.IncOrange,
				Yellow: data.IncYellow,
				GreenSoft: data.IncGreenSoft,
				GreenNormal: data.IncGreenNormal,
				GreenHard: data.IncGreenHard,
			}
		: DEFAULT_INCENTIVE_THRESHOLDS;

	const finished = data?.Finished ?? 0;
	const target = data?.Target ?? 0;
	const incPercent = data?.IncPercent ?? null;

	const noData = incPercent == null || (incPercent === 0 && target === 0);
	const pct = incPercent ?? 0;
	const zoneColor = getZoneColor(noData ? null : pct, thresholds);
	const fillColor = zoneColor.bg;

	const maxVal = Math.max(finished, target, 1);
	const targetColor = "#42A5F5";

	const chartOptions = useMemo<EChartsOption>(
		() => ({
			tooltip: {
				show: true,
				formatter: () =>
					`Finished Tons: ${finished.toFixed(0)}<br/>Target Tons: ${target.toFixed(0)}<br/>Incentive: ${pct.toFixed(1)}%`,
				borderColor: fillColor,
				appendTo: document.body,
			},
			grid: {
				left: 54,
				right: 44,
				top: 3,
				bottom: 3,
				containLabel: false,
			},
			xAxis: {
				type: "value" as const,
				min: 0,
				max: maxVal,
				show: false,
			},
			yAxis: {
				type: "category" as const,
				data: ["Target", "Finished"],
				axisLine: { show: false },
				axisTick: { show: false },
				axisLabel: { fontSize: 10, color: "#757575" },
			},
			series: [
				{
					type: "bar",
					data: [
						{
							value: target,
							itemStyle: {
								color: targetColor,
								borderRadius: [0, 4, 4, 0],
							},
						},
						{
							value: noData ? 0 : finished,
							itemStyle: {
								color: fillColor,
								borderRadius: [0, 4, 4, 0],
							},
						},
					],
					barWidth: 11,
					label: {
						show: true,
						position: "right",
						formatter: (params: { dataIndex?: number }) => {
							if (params.dataIndex === 0) return `${target.toFixed(0)}`;
							return `${finished.toFixed(0)}`;
						},
						fontSize: 10,
						fontWeight: 600,
						color: "#757575",
					},
					silent: false,
				},
			],
		}),
		[finished, target, fillColor, noData, pct, maxVal],
	);

	return (
		<Card elevation={1}>
			<CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						mb: 0.25,
					}}
				>
					<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
						Incentive
					</Typography>
					<IncPercentPill
						value={noData ? 0 : incPercent}
						thresholds={thresholds}
						isFuture={noData}
					/>
				</Box>
				<Box
					sx={{
						border: `1px solid ${SURFACE.borderLight}`,
						borderRadius: 1,
						backgroundColor: SURFACE.background,
						overflow: "hidden",
					}}
				>
					<ReactECharts
						option={chartOptions}
						notMerge
						style={{ height: 52, width: "100%" }}
						opts={{ renderer: "svg" }}
					/>
				</Box>
			</CardContent>
		</Card>
	);
};
